import { useEffect, useRef, type MouseEvent } from 'react';
import DayTrackedTimeStats, { type DayTrackedTimeItem } from '../DayTrackedTimeStats';
import { PopupBackIcon } from '../icons/PopupBackIcon';

type ShowDayTrackedStatsPopupProps = {
  closeHandler: () => void;
  value: DayTrackedTimeItem | null;
  unavailableMessage: string | null;
};

const ShowDayTrackedStatsPopup = ({
  closeHandler,
  value,
  unavailableMessage,
}: ShowDayTrackedStatsPopupProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => panelRef.current?.classList.add('opened'));
    return () => {
      cancelAnimationFrame(frame);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleClose = () => {
    if (closeTimerRef.current) return;
    requestAnimationFrame(() => panelRef.current?.classList.remove('opened'));
    closeTimerRef.current = setTimeout(closeHandler, 300);
  };

  const handlePanelClick = (event: MouseEvent) => event.stopPropagation();

  return (
    <div ref={panelRef} className="popup-overlay" onClick={handleClose}>
      <div className="popup-panel" onClick={handlePanelClick}>
        <button type="button" className="popup-back-button" onClick={handleClose}>
          <div className="popup-back-button-icon">
            <PopupBackIcon />
          </div>
          <div className="popup-back-button-text">Back</div>
        </button>
        <div className="popup-content">
          {value ? (
            <DayTrackedTimeStats value={value} />
          ) : (
            <div className="day-tracked-time-unavailable" role="alert">
              {unavailableMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowDayTrackedStatsPopup;
